import { Component, inject, type OnDestroy, type OnInit } from '@angular/core';
import {
  AbstractControl,
  FormControl,
  type ValidationErrors,
  type ValidatorFn,
  Validators,
} from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Subscription } from 'rxjs';
import type { DeepReadonly } from '../../../server/util-type';
import { SettingsService, type ThemeString } from '../settings.service';
import {
  type ChangeDateDialogComponentData,
  type OrgTaskWithUpdates,
  TaskService,
} from '../task.service';

@Component({
  selector: 'app-change-date-dialog',
  templateUrl: './change-date-dialog.component.html',
  styleUrls: ['./change-date-dialog.component.scss'],
  standalone: false,
})
export class ChangeDateDialogComponent implements OnInit, OnDestroy {
  private dialogRef = inject(MatDialogRef<ChangeDateDialogComponent>);
  private readonly data: ChangeDateDialogComponentData =
    inject(MAT_DIALOG_DATA);
  private taskService = inject(TaskService);
  private settingsService = inject(SettingsService);

  orgId: string;
  spaceId: string;
  taskId: string;
  type: 'currentAt' | 'completedAt';

  header = '';
  title = '';

  task: DeepReadonly<OrgTaskWithUpdates>;

  hour12 = true;

  datePicker = new FormControl<Date | null>(null);

  timeControl = new FormControl<string>('9:00:00', {
    validators: [Validators.required, timeValidator(this)],
    nonNullable: true,
  });

  disableSubmit = false;

  themeClass: ThemeString; // We need the theme class here because the modals are outside of the root app div

  error = false;

  subscriptions = new Subscription();

  constructor() {
    this.orgId = this.data.orgId;
    this.spaceId = this.data.spaceId;
    this.taskId = this.data.taskId;
    this.type = this.data.type;
  }

  ngOnInit() {
    const { orgId, spaceId, taskId } = this;

    const themeSubscription = this.settingsService.themeObservable.subscribe(
      (theme) => (this.themeClass = theme)
    );
    this.subscriptions.add(themeSubscription);

    this.header =
      this.type === 'completedAt'
        ? 'Change time task was set as completed'
        : this.type === 'currentAt'
        ? 'Change time task was set as current'
        : '';

    const task = this.taskService.getTaskLocalSync({ orgId, spaceId, taskId });
    if (!task) {
      this.error = true;
      return;
    }
    this.task = task;

    this.title = this.task.title;

    let timeValue: number | undefined;
    if (this.type === 'currentAt') timeValue = this.task.currentAt;
    if (this.type === 'completedAt') timeValue = this.task.completedAt;

    // If the timeValue has been removed, just set to current time
    if (!timeValue) timeValue = Date.now();

    const dateTime = new Date(timeValue);
    this.datePicker.setValue(dateTime);

    const { hour12 } = this;
    const initialTime = dateTime.toLocaleTimeString('en-US', { hour12 });
    this.timeControl.setValue(initialTime);

    const datePickerSubscribe = this.datePicker.valueChanges.subscribe(() => {
      this.disableSubmit = !!this.datePicker.errors;
    });
    this.subscriptions.add(datePickerSubscribe);

    const timeControlSubscribe = this.timeControl.valueChanges.subscribe(() => {
      this.disableSubmit = !!this.timeControl.errors;
    });
    this.subscriptions.add(timeControlSubscribe);
  }

  updateTime() {
    const datePicker = this.datePicker.value;
    if (!datePicker) return;
    const dateString =
      '' +
      datePicker.getFullYear() +
      '-' +
      (datePicker.getMonth() + 1) + // because jan = 0
      '-' +
      datePicker.getDate() +
      ' ' +
      this.timeControl.value;

    const dateObj = new Date(dateString);
    const timeValue = dateObj.valueOf();

    const update =
      this.type === 'completedAt'
        ? { completedAt: timeValue }
        : this.type === 'currentAt'
        ? { currentAt: timeValue }
        : undefined;

    if (!update) throw new Error('Type not found');

    const { orgId, spaceId, taskId } = this;
    this.taskService.updateTask({ orgId, spaceId, taskId }, update);

    this.dialogRef.close();
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
}

function timeValidator(self): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const idRegEx = new RegExp(
      self.hour12
        ? /^(0?[1-9]|1[0-2]):[0-5][0-9](:[0-5][0-9])?(\s?[APap][mM])$/
        : /^(0?[0-9]|1[0-9]|2[0-3]):[0-5][0-9](:[0-5][0-9])?$/
    );
    if (idRegEx.test(control.value)) return null;
    return { token: 'token failed validation' };
  };
}
